/**
 * Response utility functions
 */

import type { ApiResponse, ApiError } from '../types'

/**
 * Checks if response is successful
 */
export function isSuccessResponse(response: ApiResponse): boolean {
  return response.status >= 200 && response.status < 300
}

/**
 * Checks if response is a client error
 */
export function isClientError(response: ApiResponse): boolean {
  return response.status >= 400 && response.status < 500
}

/**
 * Checks if response is a server error
 */
export function isServerError(response: ApiResponse): boolean {
  return response.status >= 500 && response.status < 600
}

/**
 * Checks if response is a redirect
 */
export function isRedirect(response: ApiResponse): boolean {
  return response.status >= 300 && response.status < 400
}

/**
 * Extracts error message from response
 */
export function extractErrorMessage(response: ApiResponse): string {
  if (typeof response.data === 'string') {
    return response.data
  }

  if (typeof response.data === 'object' && response.data) {
    // Try common error message fields
    const errorFields = ['message', 'error', 'detail', 'description', 'msg']

    for (const field of errorFields) {
      if (response.data[field] && typeof response.data[field] === 'string') {
        return response.data[field]
      }
    }

    // If no standard field found, stringify the object
    return JSON.stringify(response.data)
  }

  return response.statusText || 'Unknown error'
}

/**
 * Creates standardized error from response
 */
export function createErrorFromResponse(response: ApiResponse): ApiError {
  const message = extractErrorMessage(response)
  const error = new Error(message) as ApiError

  error.response = response
  error.code = String(response.status)
  error.config = response.config

  return error
}

/**
 * Parses response headers into object
 */
export function parseResponseHeaders(headers: Headers): Record<string, string> {
  const result: Record<string, string> = {}

  headers.forEach((value, key) => {
    result[key.toLowerCase()] = value
  })

  return result
}

/**
 * Gets content type from response
 */
export function getResponseContentType(response: ApiResponse): string {
  return response.headers['content-type'] || response.headers['Content-Type'] || ''
}

/**
 * Checks if response is JSON
 */
export function isJsonResponse(response: ApiResponse): boolean {
  const contentType = getResponseContentType(response)
  return contentType.includes('application/json')
}

/**
 * Checks if response is HTML
 */
export function isHtmlResponse(response: ApiResponse): boolean {
  const contentType = getResponseContentType(response)
  return contentType.includes('text/html')
}

/**
 * Checks if response is XML
 */
export function isXmlResponse(response: ApiResponse): boolean {
  const contentType = getResponseContentType(response)
  return contentType.includes('application/xml') || contentType.includes('text/xml')
}

/**
 * Gets response size from headers
 */
export function getResponseSize(response: ApiResponse): number {
  const contentLength = response.headers['content-length'] || response.headers['Content-Length']
  return contentLength ? Number.parseInt(contentLength, 10) : 0
}

/**
 * Checks if response is compressed
 */
export function isCompressedResponse(response: ApiResponse): boolean {
  const encoding = response.headers['content-encoding'] || response.headers['Content-Encoding']
  return !!(encoding && ['gzip', 'deflate', 'br'].includes(encoding))
}

/**
 * Gets cache control directives from response
 */
export function getCacheControl(response: ApiResponse): Record<string, string | boolean> {
  const cacheControl = response.headers['cache-control'] || response.headers['Cache-Control']

  if (!cacheControl) {
    return {}
  }

  const directives: Record<string, string | boolean> = {}
  const parts = cacheControl.split(',').map(part => part.trim())

  for (const part of parts) {
    if (part.includes('=')) {
      const [key, value] = part.split('=', 2)
      directives[key!.trim()] = value!.trim().replace(/"/g, '')
    } else {
      directives[part] = true
    }
  }

  return directives
}

/**
 * Checks if response can be cached
 */
export function isCacheableResponse(response: ApiResponse): boolean {
  // Don't cache error responses
  if (!isSuccessResponse(response)) {
    return false
  }

  const cacheControl = getCacheControl(response)

  // Explicit no-cache directive
  if (cacheControl['no-cache'] || cacheControl['no-store']) {
    return false
  }

  // Private responses shouldn't be cached in shared caches
  if (cacheControl['private']) {
    return false
  }

  return true
}

/**
 * Gets response TTL from cache headers
 */
export function getResponseTTL(response: ApiResponse): number {
  const cacheControl = getCacheControl(response)

  if (cacheControl['max-age']) {
    return Number.parseInt(String(cacheControl['max-age']), 10) * 1000
  }

  // Check Expires header
  const expires = response.headers['expires'] || response.headers['Expires']
  if (expires) {
    const expiresDate = new Date(expires)
    const now = new Date()
    return Math.max(0, expiresDate.getTime() - now.getTime())
  }

  // Default TTL
  return 5 * 60 * 1000 // 5 minutes
}

/**
 * Validates response data against schema
 */
export function validateResponseData<T>(
  response: ApiResponse<T>,
  validator: (data: any) => boolean
): boolean {
  try {
    return validator(response.data)
  } catch {
    return false
  }
}

/**
 * Transforms response data
 */
export function transformResponseData<T, U>(
  response: ApiResponse<T>,
  transformer: (data: T) => U
): ApiResponse<U> {
  return {
    ...response,
    data: transformer(response.data),
  }
}

/**
 * Creates response metadata for logging/monitoring
 */
export function createResponseMetadata(response: ApiResponse): {
  status: number
  statusText: string
  contentType: string
  size: number
  compressed: boolean
  cacheable: boolean
  timestamp: number
} {
  return {
    status: response.status,
    statusText: response.statusText,
    contentType: getResponseContentType(response),
    size: getResponseSize(response),
    compressed: isCompressedResponse(response),
    cacheable: isCacheableResponse(response),
    timestamp: Date.now(),
  }
}

/**
 * Extracts pagination info from response headers
 */
export function extractPaginationInfo(response: ApiResponse): {
  page?: number
  limit?: number
  total?: number
  totalPages?: number
  hasNext?: boolean
  hasPrevious?: boolean
} | null {
  const headers = response.headers

  // Try different header formats
  const page = Number.parseInt(headers['x-page'] || headers['X-Page'] || '1', 10)
  const limit = Number.parseInt(headers['x-per-page'] || headers['X-Per-Page'] || '10', 10)
  const total = Number.parseInt(headers['x-total'] || headers['X-Total'] || '0', 10)

  if (isNaN(total)) {
    return null
  }

  const totalPages = Math.ceil(total / limit)

  return {
    page,
    limit,
    total,
    totalPages,
    hasNext: page < totalPages,
    hasPrevious: page > 1,
  }
}

/**
 * Checks if response indicates rate limiting
 */
export function isRateLimited(response: ApiResponse): boolean {
  return response.status === 429
}

/**
 * Gets retry-after value from response
 */
export function getRetryAfter(response: ApiResponse): number | null {
  const retryAfter = response.headers['retry-after'] || response.headers['Retry-After']

  if (!retryAfter) {
    return null
  }

  const seconds = Number.parseInt(retryAfter, 10)
  return isNaN(seconds) ? null : seconds * 1000 // Convert to milliseconds
}
